/**
 * STEP MOA-LIVE-VOICE-1C · Vosk-browser 기반 "라모야" 로컬 호출어 Provider.
 * STEP MOA-LIVE-VOICE-1C-FIX · 다운로드 진행률 · 타임아웃 · 재시도 · 단계별 에러.
 * =============================================================================
 * 라이선스: vosk-browser (Apache 2.0) · vosk-model-small-ko-0.22 (Apache 2.0).
 *
 * RCA (STEP 1-C 1차 QA 지연 원인)
 *   · 기존 default URL (ccoreilly.github.io/.../ko-0.22.tar.gz) 가 **HTTP 404** →
 *     vosk-browser 가 HTML 404 페이지를 .tar.gz 로 해석 시도 → **무한 hang**.
 *   · alphacephei.com 공식 .zip 은 CORS 헤더 미제공 + 포맷 불일치.
 *   · HuggingFace rhasspy 미러도 404.
 *   · 기존 코드에 타임아웃·진행률·재시도 전부 부재 → 사용자 영원히 대기.
 *
 * Fix (최소 수정)
 *   · 기본 URL 을 **백엔드 same-origin 자체 호스팅 경로** 로 교체
 *     (`/api/public/assets/vosk-ko-small.tar.gz`).  CORS 완전 우회.
 *   · HEAD 사전 체크 → 200/206 아니면 즉시 `cdn_404` 로 fail-fast.
 *   · 모델 fetch 를 **직접 수행** 하여 Content-Length + ReadableStream 로 **진행률** 획득
 *     → onProgress(0~100) 콜백 → Blob → object URL → vosk `createModel()` 전달.
 *   · 초기화 전체에 **90초 타임아웃** · 네트워크/압축 해제/Recognizer 각 단계 에러
 *     구분 (`cdn_404`/`timeout`/`decompress`/`recognizer_init_failed`/`mic_denied`).
 *   · ctx.onProgress(stage, percent, meta) 노출 — UI 가 Stage 를 구분해 안내 가능.
 *   · ctx.signal (AbortSignal) 지원 → 토글 OFF 시 fetch 중단.
 *
 * iOS 는 그대로 ios_hold.
 *
 * provider contract
 *   { init: (ctx) => Promise<void>, dispose: () => void, name: 'vosk-ko' }
 *   ctx = { onDetect, onProgress?, onError?, signal? }
 */

import { matchWakeWord, WAKE_AWAIT_MS } from '@/lib/ramoWakeWordConfig';

const _COOLDOWN_MS = 4000;
// STEP WAKEWORD-FLEX-1 · 명령 캡처 (같은 마이크 스트림 재사용 → 충돌 0).
const _PREROLL_MS = 2000;      // 호출어 직전 오디오 보존 → 연속 발화 앞부분 보호
const _CAPTURE_MAX_MS = 10000;
const _SILENCE_END_MS = 1200;
const _VAD_RMS = 0.012;

export const rmsOf = (arr) => {
  let s = 0;
  for (let i = 0; i < arr.length; i += 1) s += arr[i] * arr[i];
  return Math.sqrt(s / Math.max(1, arr.length));
};

export const encodeWav = (chunks, sampleRate) => {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const buf = new ArrayBuffer(44 + total * 2);
  const v = new DataView(buf);
  const w = (o, s) => { for (let i = 0; i < s.length; i += 1) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + total * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, total * 2, true);
  let o = 44;
  for (const c of chunks) {
    for (let i = 0; i < c.length; i += 1) {
      const x = Math.max(-1, Math.min(1, c[i]));
      v.setInt16(o, x < 0 ? x * 0x8000 : x * 0x7fff, true); o += 2;
    }
  }
  return new Blob([buf], { type: 'audio/wav' });
};

// 모델 다운로드 + Vosk 초기화 전체 타임아웃.  83MB · 3G 저속 환경 가정.
const _INIT_TIMEOUT_MS = 90 * 1000;

const _detectIOS = () => {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    return /iphone|ipad|ipod/.test(ua)
      || (/mac/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  } catch { return false; }
};

const _getModelUrl = () =>
  process.env.REACT_APP_VOSK_KO_MODEL_URL
  // STEP 1-C-FIX · same-origin 백엔드 자체 호스팅 (CORS 우회).  REACT_APP_BACKEND_URL
  // prefix 는 api 모듈이 알아서 붙이므로 여기서는 relative path 사용.
  || `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/assets/vosk-ko-small.tar.gz`;

const _withTimeout = (promise, ms, abortCtrl) => {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      try { abortCtrl?.abort?.(); } catch {}
      const err = new Error('timeout');
      err.code = 'timeout';
      reject(err);
    }, ms);
    promise.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
};

/** 진행률 추적 fetch — Content-Length 기반 ReadableStream 소비. */
const _fetchWithProgress = async (url, { signal, onProgress } = {}) => {
  // 1) HEAD precheck (선택적) — HEAD 미지원 (405) · 네트워크 실패 등은 GET 으로 fallback.
  //    STEP 1-C-FIX-2 · CloudFlare/Ingress 가 FastAPI GET-only 라우트에 HEAD 요청 시
  //    405 를 반환해 "cdn_404" 로 오인되는 regression 수정.  명시적 404/410 만 fail-fast.
  try {
    const headRes = await fetch(url, { method: 'HEAD', signal, cache: 'no-store' });
    if (headRes && (headRes.status === 404 || headRes.status === 410)) {
      const err = new Error('cdn_404');
      err.code = 'cdn_404';
      err.status = headRes.status;
      throw err;
    }
    // 그 외 상태 (200/206/405/501 등) 는 GET 결과를 신뢰.
  } catch (e) {
    // AbortError / 네트워크 오류는 아래 GET 에서 재확인.  HEAD 가 cdn_404 를
    // 명시적으로 던진 경우에만 상위로 전파.
    if (e?.code === 'cdn_404') throw e;
  }

  // 2) 실제 GET + stream progress.
  const res = await fetch(url, { signal, cache: 'no-store' });
  if (!res.ok) {
    const err = new Error(res.status === 404 ? 'cdn_404' : 'fetch_failed');
    err.code = res.status === 404 ? 'cdn_404' : 'fetch_failed';
    err.status = res.status;
    throw err;
  }
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body || !res.body.getReader) {
    const blob = await res.blob();
    onProgress?.('downloading', 100, { total: blob.size });
    return blob;
  }
  const reader = res.body.getReader();
  const chunks = [];
  let loaded = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    if (total > 0) {
      onProgress?.('downloading', Math.min(99, Math.round((loaded / total) * 100)), { loaded, total });
    } else {
      onProgress?.('downloading', 50, { loaded });
    }
  }
  onProgress?.('downloading', 100, { loaded, total });
  const type = res.headers.get('content-type') || 'application/gzip';
  return new Blob(chunks, { type });
};

export const createVoskWakeWord = (opts = {}) => {
  const name = 'vosk-ko';
  const modelUrl = opts.modelUrl || _getModelUrl();

  let _model = null;
  let _recognizer = null;
  let _audioCtx = null;
  let _micNode = null;
  let _workletNode = null;
  let _stream = null;
  let _disposed = false;
  let _lastDetectAt = 0;
  let _onDetect = null;
  let _abortCtrl = null;
  let _preroll = [];             // [{ data: Float32Array, ms }]
  let _capture = null;           // 진행 중 명령 캡처 상태
  let _waitSegmentEnd = false;   // 감지된 구간의 잔여 partial 재감지 방지
  // STEP WAKEWORD-FLEX-1-RCA · 모바일 진단용 카운터 (동작 영향 0).
  const _diag = {
    frames: 0, peakRms: 0, partials: 0, results: 0, lastText: '', lastAt: 0,
    lastMatch: null, detects: 0, lastSkip: '', handoff: '',
  };

  const init = async (ctx = {}) => {
    _onDetect = typeof ctx.onDetect === 'function' ? ctx.onDetect : null;
    const onProgress = typeof ctx.onProgress === 'function' ? ctx.onProgress : () => {};

    if (_detectIOS()) {
      const err = new Error('ios_hold'); err.code = 'ios_hold'; throw err;
    }
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('browser_unsupported');
    }

    _abortCtrl = new AbortController();
    const _signal = _abortCtrl.signal;

    // Lazy import — 번들 코드 스플릿.
    let voskMod;
    try {
      onProgress('loading_sdk', 0);
      voskMod = await import('vosk-browser');
      onProgress('loading_sdk', 100);
    } catch (e) {
      const err = new Error('vosk_load_failed'); err.code = 'vosk_load_failed'; err.cause = e; throw err;
    }
    if (_disposed) return;

    // 모델 다운로드 (진행률 + 타임아웃) — Blob 반환은 **HTTP 캐시 워밍업 용도** 로만 사용.
    // STEP 1-C-FIX-3 · RCA: vosk-browser Worker 는 base64 data URL 로 생성되어 opaque
    // origin 을 가지므로 메인-스레드에서 만든 blob: URL 에 접근 불가 → XHR 실패 →
    // createModel 이 { result:false } 로 reject.  공식 예제대로 **직접 URL 문자열** 을
    // 전달해야 Worker XHR 가 정상 동작.  우리 fetch 는 캐시 워밍업 + 진행률 표시 용.
    try {
      await _withTimeout(
        _fetchWithProgress(modelUrl, { signal: _signal, onProgress }),
        _INIT_TIMEOUT_MS,
        _abortCtrl,
      );
    } catch (e) {
      if (e?.code === 'cdn_404') throw e;
      if (e?.code === 'timeout') throw e;
      const err = new Error('fetch_failed'); err.code = 'fetch_failed'; err.cause = e; throw err;
    }
    if (_disposed) return;

    // 모델 초기화 — Worker 가 캐시된 모델을 XHR 로 재-fetch (same-origin · instant).
    try {
      onProgress('initializing_model', 0);
      _model = await voskMod.createModel(modelUrl);
      onProgress('initializing_model', 100);
    } catch (e) {
      const err = new Error('decompress_failed'); err.code = 'decompress_failed'; err.cause = e; throw err;
    }
    if (_disposed) { try { _model?.terminate?.(); } catch {} _model = null; return; }

    // 마이크 스트림 — LiveKit 과 독립.
    try {
      onProgress('mic', 0);
      _stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          sampleRate: 16000,
        },
      });
      onProgress('mic', 100);
    } catch (e) {
      try { _model?.terminate?.(); } catch {} _model = null;
      const err = new Error(e?.name === 'NotAllowedError' ? 'mic_denied' : 'mic_error');
      err.cause = e; throw err;
    }
    if (_disposed) { _teardownStream(); try { _model?.terminate?.(); } catch {} return; }

    // Recognizer — vosk-browser@0.0.8 공식 API 는 Model 인스턴스의 KaldiRecognizer getter
    // (`new _model.KaldiRecognizer(sampleRate)`).  module top-level 에는 KaldiRecognizer
    // 가 **없다** (keys=['Model','createModel']).  STEP MOA-LIVE-VOICE-1C-RCA Phase 2
    // · 2026-02 세션 · 1 줄 최소 수정 (대표 승인).
    try {
      onProgress('recognizer', 0);
      _recognizer = new _model.KaldiRecognizer(16000);
      _recognizer.setWords(true);
      _recognizer.on('result', (msg) => {
        _diag.results += 1; _diag.lastText = `[final] ${msg?.result?.text || ''}`; _diag.lastAt = Date.now();
        if (_waitSegmentEnd) { _waitSegmentEnd = false; _diag.lastSkip = 'segment_end'; return; }
        _handleResult(msg?.result?.text || '');
      });
      _recognizer.on('partialresult', (msg) => {
        const p = msg?.result?.partial || '';
        if (p) { _diag.partials += 1; _diag.lastText = `[partial] ${p}`; _diag.lastAt = Date.now(); }
        if (_waitSegmentEnd) { _diag.lastSkip = 'wait_segment_end'; return; }
        _handleResult(msg?.result?.partial || '');
      });
      onProgress('recognizer', 100);
    } catch (e) {
      _teardownStream(); try { _model?.terminate?.(); } catch {} _model = null;
      const err = new Error('recognizer_init_failed'); err.code = 'recognizer_init_failed'; err.cause = e; throw err;
    }

    // AudioWorklet / AudioContext.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      _audioCtx = new Ctx({ sampleRate: 16000 });
      if (_audioCtx.state === 'suspended') { try { await _audioCtx.resume(); } catch {} }
      _micNode = _audioCtx.createMediaStreamSource(_stream);
      const buf = 8192;
      _workletNode = _audioCtx.createScriptProcessor(buf, 1, 1);
      _workletNode.onaudioprocess = (ev) => {
        if (_disposed || !_recognizer) return;
        _diag.frames += 1;
        try { _diag.peakRms = Math.max(_diag.peakRms * 0.8, rmsOf(ev.inputBuffer.getChannelData(0))); } catch {}
        try { _feedCapture(ev.inputBuffer); } catch {}
        try { _recognizer.acceptWaveform(ev.inputBuffer); } catch {}
      };
      _micNode.connect(_workletNode);
      _workletNode.connect(_audioCtx.destination);
      onProgress('ready', 100);
    } catch (e) {
      dispose();
      const err = new Error('audio_pipeline_failed'); err.code = 'audio_pipeline_failed'; err.cause = e; throw err;
    }
  };

  const _handleResult = (text) => {
    if (!text) return;
    if (!_onDetect) { _diag.lastSkip = 'no_handler'; return; }
    if (_capture) { _diag.lastSkip = 'capturing'; return; }
    const m = matchWakeWord(text);
    _diag.lastMatch = { text, matched: m.matched, alias: m.alias };
    if (!m.matched) return;
    const now = Date.now();
    if (now - _lastDetectAt < _COOLDOWN_MS) { _diag.lastSkip = 'cooldown'; return; }
    _lastDetectAt = now;
    _waitSegmentEnd = true;
    _diag.detects += 1;
    _diag.lastSkip = '';
    try { _onDetect({ transcript: text, alias: m.alias, command: m.command, wake: 'vosk-ko' }); } catch {}
  };

  const _feedCapture = (inputBuffer) => {
    const data = new Float32Array(inputBuffer.getChannelData(0));
    const ms = (data.length / inputBuffer.sampleRate) * 1000;
    if (!_capture) {
      _preroll.push({ data, ms });
      let sum = _preroll.reduce((n, c) => n + c.ms, 0);
      while (_preroll.length > 1 && sum - _preroll[0].ms >= _PREROLL_MS) { sum -= _preroll[0].ms; _preroll.shift(); }
      return;
    }
    const c = _capture;
    c.chunks.push(data);
    c.elapsed += ms;
    if (rmsOf(data) >= _VAD_RMS) {
      if (!c.speech) { c.speech = true; try { c.onSpeech?.(); } catch {} }
      c.silence = 0;
    } else if (c.speech) {
      c.silence += ms;
    }
    if (!c.speech && c.elapsed >= c.timeoutMs) return _finishCapture('timeout');
    if (c.speech && c.silence >= _SILENCE_END_MS) return _finishCapture('speech');
    if (c.elapsed >= _CAPTURE_MAX_MS) return _finishCapture(c.speech ? 'speech' : 'timeout');
    return undefined;
  };

  const _finishCapture = (reason) => {
    const c = _capture;
    if (!c) return;
    _capture = null;
    _preroll = [];
    _lastDetectAt = Date.now();
    const blob = reason === 'speech'
      ? encodeWav(c.chunks, _audioCtx?.sampleRate || 16000)
      : null;
    c.resolve({ blob, reason });
  };

  /** 호출어 이후 명령 캡처 — 직전 2초 + 이후 발화를 WAV 로 반환. 발화 없으면 blob=null. */
  const captureCommand = ({ timeoutMs = WAKE_AWAIT_MS, hasCommandHint = false, onSpeech } = {}) => {
    if (_disposed || !_audioCtx) return Promise.resolve({ blob: null, reason: 'aborted' });
    if (_capture) return Promise.resolve({ blob: null, reason: 'busy' });
    return new Promise((resolve) => {
      _capture = {
        chunks: _preroll.map((c) => c.data),
        elapsed: 0, silence: 0,
        speech: !!hasCommandHint,
        timeoutMs, onSpeech, resolve,
      };
      if (hasCommandHint) { try { onSpeech?.(); } catch {} }
    });
  };

  const _teardownStream = () => {
    try { _stream?.getTracks?.().forEach((t) => { try { t.stop(); } catch {} }); } catch {}
    _stream = null;
  };

  const dispose = () => {
    _disposed = true;
    if (_capture) { const r = _capture.resolve; _capture = null; try { r({ blob: null, reason: 'aborted' }); } catch {} }
    _preroll = [];
    try { _abortCtrl?.abort(); } catch {}
    _abortCtrl = null;
    try { _workletNode?.disconnect(); } catch {}
    try { _micNode?.disconnect(); } catch {}
    try { _audioCtx?.close(); } catch {}
    _workletNode = null; _micNode = null; _audioCtx = null;
    _teardownStream();
    try { _recognizer?.remove?.(); } catch {}
    _recognizer = null;
    try { _model?.terminate?.(); } catch {}
    _model = null;
    _onDetect = null;
  };

  const getDiag = () => {
    const track = _stream?.getAudioTracks?.()[0];
    return {
      ..._diag,
      ctxState: _audioCtx?.state || 'none',
      sampleRate: _audioCtx?.sampleRate || 0,
      track: track ? `${track.readyState}${track.muted ? '/muted' : ''}${track.enabled ? '' : '/disabled'}` : 'none',
      capturing: !!_capture,
    };
  };
  const setHandoff = (reason) => { _diag.handoff = `${new Date().toLocaleTimeString()} ${reason}`; };

  return { init, dispose, captureCommand, getDiag, setHandoff, name };
};

export const voskIsIOS = _detectIOS;

export default createVoskWakeWord;
